import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Button, Spinner, Alert } from 'react-bootstrap';
import axios from 'axios';
import Input from '../../components/Input';
import { InputF } from '../../components/Input';
import Env from '../../hooks/Env';
import './UpdateVoyage.css';
import { Alert2 } from '../../components/Alerte';
import ErrorPage from '../../components/Error';

const UpdateVoyage = () => {
    const { uid } = useParams();
    const navigate = useNavigate();
    const API_URL = Env().API_URL;

    const [loading, setLoading] = useState(true);
    const [dataForm, setDataForm] = useState(null);
    const [selectedFiles, setSelectedFiles] = useState({});
    const [visibleFiles, setVisibleFiles] = useState({
        compte_rendu: false,
        arrete: false,
        justificatif: false,
    });
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState(null);
    const [statusError, setStatusError] = useState(null);
    const maxFileSize = 1024 * 1024 * 2; // 2Mo

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const res = await axios.get(`${API_URL}/voyage/uid/${uid}`);
                if (res.status === 200) {
                    setDataForm(res.data || {});
                    setVisibleFiles({
                        compte_rendu: !!res.data?.compte_rendu,
                        arrete: !!res.data?.arrete,
                        justificatif: !!res.data?.justificatif,
                    });
                } else {
                    setError('Impossible de récupérer les données');
                }
            } catch (err) {
                console.error(err);
                setStatusError(err?.response?.status || 500);
                setError('Erreur lors de la récupération des données');
            } finally {
                setLoading(false);
            }
        };

        if (uid) fetchData();
    }, [uid, API_URL]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setDataForm(prev => ({ ...(prev || {}), [name]: value }));
    };

    const handleFileChange = (e, key) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > maxFileSize) {
            alert('Fichier trop volumineux (max 2Mo)');
            return;
        }
        setSelectedFiles(prev => ({ ...prev, [key]: file }));
        setVisibleFiles(prev => ({ ...prev, [key]: true }));
    };

    const removeFile = (key) => {
        // simply remove the newly selected file
        setSelectedFiles(prev => {
            const copy = { ...prev };
            delete copy[key];
            return copy;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSubmitting(true);
            setError(null);
            const formData = new FormData();

            // append non-file fields (but exclude file fields that weren't modified)
            const fileFields = ['compte_rendu', 'arrete', 'justificatif'];
            for (let key in dataForm) {
                if (!Object.prototype.hasOwnProperty.call(dataForm, key)) continue;
                // skip internal fields and file fields
                if (key === 'uid' || key === 'date_depot' || key === 'date_update' || fileFields.includes(key)) continue;
                const val = dataForm[key];
                if (val !== null && val !== undefined) formData.append(key, val);
            }

            // append only if new file was selected (not just existing file)
            fileFields.forEach(k => {
                if (selectedFiles[k] && selectedFiles[k] instanceof File) {
                    formData.append(k, selectedFiles[k]);
                }
            });

            const res = await axios.post(`${API_URL}/voyage/update`, formData, {
                headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
            });

            if (res.status === 200) {
                setSuccess(true);
                setMessage(res.data?.message || 'Dossier mis à jour');
                setSubmitting(false);
            } else {
                setError('Erreur lors de la mise à jour');
                setSubmitting(false);
            }
        } catch (err) {
            console.error(err);
            setError('Erreur lors de la mise à jour');
            setSubmitting(false);
        }
    };

    if (loading) return (
        <div className="container text-center mt-4">
            <Spinner animation="border" role="status"><span className="visually-hidden">Chargement...</span></Spinner>
            <p className="mt-2">Chargement des données...</p>
        </div>
    );

    if (error && !dataForm) return (
        <div className="container mt-5">
            <ErrorPage errorCode={statusError} />
        </div>
    );

    return (
        <div className="container mt-3 mb-4" id="updateVoyage">
            <div className="update-voyage-header">
                <h5>Mise à jour du dossier - Voyage</h5>
            </div>

            {success && <Alert variant="success" onClose={() => setSuccess(false)} dismissible>{message}</Alert>}
            {success && <Alert2 message={message} />}
            {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}

            <Form onSubmit={handleSubmit} className="update-form">
                {/* Informations personnelles */}
                <div className="form-section">
                    <h6>Informations personnelles</h6>
                    <Input type='email' value={dataForm?.email || ''} label="Adresse mail" name='email' onChange={handleChange} disabled />
                    <div className='row'>
                        <Input className="col-md-6" value={dataForm?.prenom || ''} type='text' label="Prénom(s)" name='prenom' onChange={handleChange} />
                        <Input className="col-md-6" value={dataForm?.nom || ''} type='text' label="Nom" name='nom' onChange={handleChange} />
                    </div>
                    <div className='row'>
                        <Input className="col-md-6" value={dataForm?.matricule || ''} type='text' label="Matricule" name='matricule' onChange={handleChange} />
                        <Input className="col-md-6" value={dataForm?.telephone || ''} type='text' label="Téléphone" name='telephone' onChange={handleChange} />
                    </div>
                </div>

                {/* Grade */}
                <div className="form-section">
                    <h6>Grade et thématiques</h6>
                    <Form.Group className="mb-3">
                        <Form.Label className='fw-medium'>Grade</Form.Label>
                        <Form.Select name='grade' value={dataForm?.grade || ''} onChange={handleChange}>
                            <option value="">--- Sélectionner ---</option>
                            <option>Professeur Titulaire</option>
                            <option>Professeur Assimilé</option>
                            <option>Maître Conférences Titulaires</option>
                            <option>Maître Conférences Assimilées</option>
                            <option>Maître Conférences Stagiaires</option>
                        </Form.Select>
                    </Form.Group>
                    <Input value={dataForm?.theme_recherche || ''} type='text' label="Thèmes de recherche" name='theme_recherche' onChange={handleChange} />
                </div>

                {/* Études */}
                <div className="form-section">
                    <h6>Études</h6>
                    <Form.Group className="mb-3">
                        <Form.Label className='fw-medium'>Diplôme</Form.Label>
                        <Form.Select name='diplome' value={dataForm?.diplome || ''} onChange={handleChange}>
                            <option value="">--- Sélectionner ---</option>
                            <option>Doctorat</option>
                            <option>Master</option>
                        </Form.Select>
                    </Form.Group>
                    <Input value={dataForm?.etablissement_diplome || ''} type='text' label="Établissement d'obtention du diplôme" name='etablissement_diplome' onChange={handleChange} />
                    <div className='row'>
                        <Input className="col-md-6" value={dataForm?.date_diplome || ''} type='date' label="Date d'obtention du diplôme" name='date_diplome' onChange={handleChange} />
                        <Input className="col-md-6" value={dataForm?.lieu_diplome || ''} type='text' label="Lieu d'obtention du diplôme" name='lieu_diplome' onChange={handleChange} />
                    </div>
                </div>

                {/* Informations professionnelles */}
                <div className="form-section">
                    <h6>Informations professionnelles</h6>
                    <Form.Group className="mb-3">
                        <Form.Label className='fw-medium'>Fonctions occupées</Form.Label>
                        <Form.Select name='fonction' value={dataForm?.fonction || ''} onChange={handleChange}>
                            <option value="">--- Sélectionner ---</option>
                            <option>Recteur</option>
                            <option>Vice-recteur</option>
                            <option>Directeur</option>
                            <option>Directeur d'école</option>
                            <option>Chef de département</option>
                            <option>Coordonnateur</option>
                            <option>Enseignent-chercheur</option>
                        </Form.Select>
                    </Form.Group>
                    <Input value={dataForm?.date_recrutement || ''} type='date' label="Date de recrutement" name='date_recrutement' onChange={handleChange} />
                </div>

                {/* Partenariats nationaux */}
                <div className="form-section">
                    <h6>Partenariat et réseaux de recherche au niveau national</h6>
                    <div className='row'>
                        <Input className="col-md-6" value={dataForm?.structure1 || ''} type='text' label="Nom de la structure 1" name='structure1' onChange={handleChange} required={false} />
                        <Input className="col-md-6" value={dataForm?.naturestructure1 || ''} type='text' label="Nature de la structure 1" name='naturestructure1' onChange={handleChange} required={false} />
                    </div>
                    <div className='row'>
                        <Input className="col-md-6" value={dataForm?.structure2 || ''} type='text' label="Nom de la structure 2" name='structure2' onChange={handleChange} required={false} />
                        <Input className="col-md-6" value={dataForm?.naturestructure2 || ''} type='text' label="Nature de la structure 2" name='naturestructure2' onChange={handleChange} required={false} />
                    </div>
                </div>

                {/* Partenariats internationaux */}
                <div className="form-section">
                    <h6>Partenariat et réseaux de recherche au niveau international</h6>
                    <div className='row'>
                        <Input className="col-md-6" value={dataForm?.structureinter1 || ''} type='text' label="Nom de la structure 1" name='structureinter1' onChange={handleChange} required={false} />
                        <Input className="col-md-6" value={dataForm?.naturestructureinter1 || ''} type='text' label="Nature de la structure 1" name='naturestructureinter1' onChange={handleChange} required={false} />
                    </div>
                    <div className='row'>
                        <Input className="col-md-6" value={dataForm?.structureinter2 || ''} type='text' label="Nom de la structure 2" name='structureinter2' onChange={handleChange} required={false} />
                        <Input className="col-md-6" value={dataForm?.naturestructureinter2 || ''} type='text' label="Nature de la structure 2" name='naturestructureinter2' onChange={handleChange} required={false} />
                    </div>
                </div>

                {/* Informations voyage */}
                <div className="form-section">
                    <h6>Informations du voyage d'étude</h6>
                    <Input value={dataForm?.annne_voyage || ''} type='text' label="Année d'octroi du dernier voyage d'étude" name='annne_voyage' onChange={handleChange} />
                    <Input value={dataForm?.but_voyage || ''} type='text' label="But du voyage d'étude demandé" name='but_voyage' onChange={handleChange} />
                    <Input value={dataForm?.adresse_etranger || ''} type='text' label="Adresse complète de l'établissement d'accueil à l'étranger" name='adresse_etranger' onChange={handleChange} />
                    <Input value={dataForm?.responsable_accueil || ''} type='text' label="Responsable du laboratoire ou du centre d'accueil" name='responsable_accueil' onChange={handleChange} />
                    <Input value={dataForm?.email_responsable || ''} type='email' label="Adresse e-mail du responsable" name='email_responsable' onChange={handleChange} />
                    <Input value={dataForm?.adresse_responsable || ''} type='text' label="Adresse complète du responsable" name='adresse_responsable' onChange={handleChange} />
                    <Input value={dataForm?.pays || ''} type='text' label="Pays de destination finale" name='pays' onChange={handleChange} />
                    <Input value={dataForm?.ville_destination || ''} type='text' label="Ville de destination finale" name='ville_destination' onChange={handleChange} />
                </div>

                {/* Documents */}
                <div className="form-section">
                    <h6>Documents</h6>
                    <div className="mb-3">
                        <label className="form-label fw-medium">Compte rendu du dernier voyage</label>
                        {visibleFiles.compte_rendu && !selectedFiles.compte_rendu && dataForm?.compte_rendu && (
                            <div className="mb-2">
                                <span style={{ color: 'green', fontWeight: '500' }}>
                                    {dataForm.compte_rendu.split('/').pop()}
                                </span>
                            </div>
                        )}
                        <InputF type='file' name='compte_rendu' onChange={(e) => handleFileChange(e, 'compte_rendu')} />
                    </div>

                    <div className="mb-3">
                        <label className="form-label fw-medium">Justificatifs du dernier voyage</label>
                        {visibleFiles.justificatif && !selectedFiles.justificatif && dataForm?.justificatif && (
                            <div className="mb-2">
                                <span style={{ color: 'green', fontWeight: '500' }}>
                                    {dataForm.justificatif.split('/').pop()}
                                </span>
                            </div>
                        )}
                        <InputF type='file' name='justificatif' onChange={(e) => handleFileChange(e, 'justificatif')} />
                    </div>

                    <div className="mb-3">
                        <label className="form-label fw-medium">Arrêté rectoral accordant le dernier voyage</label>
                        {visibleFiles.arrete && !selectedFiles.arrete && dataForm?.arrete && (
                            <div className="mb-2">
                                <span style={{ color: 'green', fontWeight: '500' }}>
                                    {dataForm.arrete.split('/').pop()}
                                </span>
                            </div>
                        )}
                        <InputF type='file' name='arrete' onChange={(e) => handleFileChange(e, 'arrete')} />
                    </div>
                </div>

                <div className="form-actions mt-4">
                    <Button variant="secondary" onClick={() => navigate(-1)}>Annuler</Button>
                    <Button type="submit" variant="primary" disabled={submitting}>
                        {submitting ? <><Spinner animation="border" size="sm" className="me-2" /> Mise à jour...</> : 'Mettre à jour'}
                    </Button>
                </div>
            </Form>
        </div>
    );
};

export default UpdateVoyage;
